import {noop} from 'lodash';
import {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {MODULE_TYPES} from '../../../../../constants';
import {hasModule} from '../../../../shared/util/ModuleUtil';
import {EntityType, EntityTypeLabels} from '../../Constants';
import {CustomFieldDefinition} from '../../types';

export type AvaRefOption = {
	label: string;
	value: number | null;
	disabled: boolean | undefined;
};

const nullAvaRef = {label: 'None', value: null, disabled: false};

export const useAvaRefOptions = (
	selectedEntityType: EntityType,
	customFieldDefinitions: Pick<CustomFieldDefinition, 'entityType' | 'avaRef'>[],
	initialValue: number | null = null
): {
	selectedOption: AvaRefOption;
	setSelectedOption: (value: AvaRefOption) => void;
	options: AvaRefOption[] | null;
} => {
	const {formatMessage} = useIntl();
	const [avaRefValue, setAvaRefValue] = useState<number | null>(initialValue);

	const showAvaRefField =
		hasModule(MODULE_TYPES.SISENSE) &&
		(selectedEntityType === 'PROJECT' || selectedEntityType === 'TASK' || selectedEntityType === 'PHASE');
	const numOfOptions = selectedEntityType === 'PROJECT' ? 20 : 10;

	const avaRefOptions = useMemo(
		() => [
			nullAvaRef,
			...Array.from({length: numOfOptions}, (_, i) => i + 1).map(value => ({
				label: formatMessage(
					{id: 'settings.custom-fields.create_modal.ava_ref_item'},
					{entityType: EntityTypeLabels[selectedEntityType], index: value}
				),
				value,
				disabled: !!customFieldDefinitions.find(cf => cf.entityType === selectedEntityType && cf.avaRef === value),
			})),
		],
		[customFieldDefinitions, selectedEntityType]
	);

	if (!showAvaRefField) {
		return {
			selectedOption: nullAvaRef,
			setSelectedOption: noop,
			options: null,
		};
	}

	return {
		selectedOption: avaRefOptions.find(option => option.value === avaRefValue) || nullAvaRef,
		setSelectedOption: (refOption: AvaRefOption) => setAvaRefValue(refOption.value),
		options: avaRefOptions,
	};
};
